import { useCallback } from 'react';

import type { TMessage } from '@stores/chat-domain.types';

import { useChatStore } from '@stores/chat.store';
import { chatWebSocketService } from '../chat-websocket.service';
import { WS_ACTIONS } from '../utils/chat-websocket.constants';
import type { TPaginationKey } from '../chat-websocket.types';
import type {
  TUseChatMessagesReturn,
  TUpdateMessageStatusParams,
  TUpdateActiveConversationParams,
  TUpdateConversationsParams,
} from './use-chat-messages.types';

/** Retorna ações para enviar, buscar e atualizar mensagens via WebSocket. */
export function useChatMessages(): TUseChatMessagesReturn {
  const sendMessage = (data: string, chatId: string) => {
    chatWebSocketService.send({
      action: WS_ACTIONS.sendMessage,
      chatId,
      data,
    });
  };

  const fetchOlderMessageBatch = useCallback(
    (chatId: string, lastKey: TPaginationKey) => {
      useChatStore.getState().setMessageListLoading(chatId);
      chatWebSocketService.send({
        action: WS_ACTIONS.getMessages,
        chatId,
        lastKey,
      });
    },
    [],
  );

  const loadOlderMessages = useCallback(() => {
    useChatStore.getState().loadOlderMessages();
  }, []);

  const updateMessageStatus = useCallback(
    (params: TUpdateMessageStatusParams) => {
      chatWebSocketService.send({
        action: WS_ACTIONS.postUpdateStatus,
        chatId: params.chatId,
        UserIdToUpdateMessage: params.UserIdToUpdateMessage,
      });
    },
    [],
  );

  const updateActiveConversation = useCallback(
    (params: TUpdateActiveConversationParams) => {
      useChatStore.getState().setNewMessageFulfilled({
        messages: [params.source as TMessage],
        chatId: params.chatId,
        chatType: params.chatType,
        itsMyMessage: true,
      });
    },
    [],
  );

  const updateConversations = (params: TUpdateConversationsParams) => {
    const store = useChatStore.getState();
    if (params.chatType === 'to') {
      store.setConversationListUpdated('to', params.conversations);
    } else {
      store.setConversationListUpdated('from', params.conversations);
    }
  };

  return {
    sendMessage,
    fetchOlderMessageBatch,
    loadOlderMessages,
    updateMessageStatus,
    updateActiveConversation,
    updateConversations,
  };
}
